<template>
  <div class="img-upld">
    <v-card v-if="image" height="100%">
      <v-img
        :src="imageLink"
        class="white--text align-end"
        gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
        height="100%"
        @click="showImage"
      >
      </v-img>
      <div v-if="label" class="img-label">
        <v-label>{{ label }}</v-label>
      </div>
      <v-icon
        v-if="!readonly"
        class="clear-btn"
        large
        @click="clearImage()"
        :title="$t('common.fields.photo.clear')"
        >{{ iconClear }}</v-icon
      >
      <v-btn
        v-if="download"
        class="download-btn"
        color="#6ae86e"
        icon
        :loading="imageDownloading"
        @click="downloadImage"
      >
        <v-icon :title="$t('common.btns.save')" large>
          mdi-download
        </v-icon>
      </v-btn>
    </v-card>
    <v-card
      v-else
      outlined
      height="100%"
      :loading="loading"
      :class="{ disabled: isDisabled }"
    >
      <div v-if="label" class="img-label">
        <v-label>{{ label }}</v-label>
      </div>
      <div v-if="readonly" class="no-photo">
        <v-icon large>mdi-camera-off</v-icon>
        Нет фото
      </div>
      <div v-else class="upload-btn" @click="uploadFile(hash)">
        <div class="upload-icon">
          <v-icon large>
            {{ iconUpload }}
          </v-icon>
          Загрузить фото
        </div>
      </div>
    </v-card>
    <v-alert v-if="errorText" class="error-t" type="error">
      {{ errorText }}
    </v-alert>
    <input type="file" :ref="hash" @change="handleFileChange" />
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import axios from '@/plugins/axios';

export default {
  name: 'ImageUploader',
  data() {
    return {
      availableTypes: ['image/png', 'image/jpeg', 'image/jpg'],
      sizeLimit: 5242880,
      iconUpload: 'mdi-upload',
      iconClear: 'mdi-delete-forever',
      image: null,
      imageLink: null,
      imageDownloading: false,
      loading: false,
      error: false,
      errorText: null,
    };
  },
  props: {
    photo: Object,
    rules: Array,
    disabled: Boolean,
    label: String,
    readonly: Boolean,
    download: Boolean,
  },
  computed: {
    hash() {
      const random = Math.floor(Math.random() * (+1 - +100000)) + +1;
      return random;
    },
    isDisabled() {
      if (this.disabled) {
        return true;
      }
      return false;
    },
  },
  watch: {
    // eslint-disable-next-line
    photo: {
      // the callback will be called immediately after the start of the observation
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.image = newVal.name;
          this.imageLink = newVal.link;
        }
      },
    },
  },
  methods: {
    ...mapMutations({
      setImageViewerLink: 'global/setImageViewerLink',
      setImageViewer: 'global/setImageViewer',
      setImageUploader: 'global/setImageUploader',
    }),
    ...mapActions({
      uploadPhotos: 'global/uploadPhotos',
    }),
    uploadFile(index) {
      if (this.disabled) {
        return true;
      }
      const docRef = `${index}`;
      this.$refs[docRef].click();
      return true;
    },
    async handleFileChange(e) {
      // check file
      const file = e.srcElement.files[0];
      try {
        this.error = false;
        this.errorText = '';
        if (file && typeof file !== 'undefined') {
          // check file format
          if (this.availableTypes.indexOf(file.type) < 0) {
            throw new Error(this.$t('common.fields.validation.photo.format'));
          }
          if (file.size > this.sizeLimit || file.size === 0) {
            throw new Error(this.$t('common.fields.validation.photo.size'));
          }
        }
        this.loading = true;
        this.setImageUploader({
          uploaderHash: this.hash,
          isUploading: true,
        });
        const formData = new FormData();
        // send file to server
        formData.append('images[]', file);
        const images = await this.uploadPhotos(formData);

        if (images.length && images.length > 0) {
          this.$refs[this.hash].value = null;
          this.$emit('update:photo', images[0]);
        }
        this.loading = false;
        this.setImageUploader({
          uploaderHash: this.hash,
          isUploading: false,
        });
      } catch (error) {
        this.loading = false;
        this.error = true;
        this.$refs[this.hash].value = null;
        this.errorText = error.message;
      }
    },
    validate() {
      let isValid = true;
      if (this.rules && this.rules.length > 0) {
        this.rules.some((rule) => {
          const msg = rule(this.photo);
          if (typeof msg === 'string') {
            this.errorText = msg;
            isValid = false;
            return true;
          }
          this.errorText = null;
          return false;
        });
      }
      return isValid;
    },
    clearImage() {
      this.image = null;
      this.imageLink = null;
      this.$refs[this.hash].file = null;
      this.$emit('update:photo', null);
    },
    showImage() {
      this.setImageViewerLink(this.imageLink);
      this.setImageViewer(true);
    },
    async downloadImage() {
      this.imageDownloading = true;
      const downloadLink = this.imageLink.split('storage')[1];
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';

      const { data } = await axios.get(
        `image/download?path=/storage${downloadLink}`,
        {
          headers: { Authorization: `Bearer ${token}`, Lang: lang },
          responseType: 'blob',
        },
      );

      const a = document.createElement('a');
      a.href = URL.createObjectURL(data);
      a.setAttribute('download', this.photo.name);
      a.click();

      this.imageDownloading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.img-upld {
  height: 100%;
  input[type='file'] {
    display: none;
  }
  .clear-btn,
  .download-btn {
    background: #fafafa69;
    position: absolute;
    right: 5px;
    border-radius: 0 !important;
    cursor: pointer;
  }
  .clear-btn {
    color: #ffa726;
    top: 5px;
  }
  .download-btn {
    bottom: 5px;
    text-decoration: none;
  }
  .upload-btn {
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
  .upload-icon {
    margin: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    margin-right: -50%;
    transform: translate(-50%, -50%);
    cursor: pointer;
  }
  .disabled {
    .upload-btn,
    .upload-icon {
      cursor: not-allowed;
    }
    background-color: #cacaca;
    color: #4a4a4a;
  }
  .error-border {
    border: solid 1px red;
  }
  .error-t {
    top: -56px;
    width: 100%;
  }
  .img-label {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    padding: 11px;
    background-color: #e9e9e9e3;
    border-bottom-left-radius: 0px !important;
    border-bottom-right-radius: 0px !important;
    font-size: 14px;
    line-height: 1.2;
    label {
      font-size: 14px;
    }
  }
  .no-photo {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    .v-icon {
      margin-right: 10px;
    }
  }
}
</style>
